import faker from 'faker/locale/en';
// Seed faker so module-level fixtures (fakeEnterpriseAttributes, fakeEnterpriseId,
// fakeOfficeId, fakeBuildingId, etc.) are deterministic regardless of how Jest
// distributes test files across workers. Without this, the order in which Jest
// imports modules shifts the global faker state, and `fakeEnterpriseAttributes`
// — which uses `randomIn({1,2})` for its length and `fakeAttribute()` for content —
// resolves to different values run-to-run, breaking tests that look up by
// `fakeEnterpriseAttributes[0].referenceName`. Matches the established pattern in
// shared/data.ts, plans/api.ts, announcements/fakers.ts, vehicles/fakers.ts.
faker.seed(4774);
import { times } from 'lodash';
import { AttributeProps } from '@commutifi/models/Attributes';
import { Countries } from '@commutifi/constants/Countries';
import { EnterpriseKind } from '@commutifi/models/Enterprises';
import { type BuildingFilter } from 'api/modules/analytics/types/common';
import { IOrganization } from 'shared/interfaces';
import { fakeCommutifiEmail, fakeFullName, fakeTimezone, randomIn } from '../../_utils';
import { fakeEnterprise as generateFakeEnterprise, fakeAttribute, fakeShippingRates, fakeFundingInstructions, fakeBalances, fakeInventoryFilters } from './fakers';
import { fakeWorksite } from '../worksites/fakers';
export const fakeEnterpriseId = faker.datatype.uuid();
const fakeBuildingId = faker.datatype.uuid();
export const fakeAccountBuilding = {
  id: fakeBuildingId,
  address: '1111 Pearl St, Boulder, CO 80302, USA',
  city: 'Boulder',
  country: Countries.UnitedStates,
  createdAt: '2020-01-27T16:57:44.452Z',
  updatedAt: '2020-07-14T00:40:33.536Z',
  lastLeedOccupantSubmission: null,
  lastLeedTransportSubmission: null,
  location: {
    lat: 40.0179654,
    lng: -105.2809452,
    toJSON: () => ({
      lat: 40.0179654,
      lng: -105.2809452
    })
  },
  name: undefined,
  postalCode: '80302',
  state: 'Colorado',
  street: 'Pearl Street',
  streetNumber: '1111',
  timezone: fakeTimezone()
};
export const fakeEnterpriseDashboardResponse = {
  enterprise: generateFakeEnterprise({
    id: fakeEnterpriseId,
    createdAt: faker.date.past().toISOString(),
    updatedAt: faker.date.past().toISOString(),
    billingEmail: 'testbilling@commutifi.com',
    identityProviderId: undefined,
    canRegisterManagers: false
  })
};
// Simply unwrap faked enterprise from the /dashboard endpoint fake response to make it easier to use
export const fakeEnterprise = fakeEnterpriseDashboardResponse.enterprise;
export const fakeOrganizationDashboardResponse = {
  organization: {
    ...fakeEnterpriseDashboardResponse.enterprise,
    kind: EnterpriseKind.organization
  }
};

/**
 * Faked /enterprises/:entId/sub-enterprises payload used for
 * enterprises list view
 *
 * Min 2 cause we also use those to generate fake plan permissions
 * and for tests purposes we want at least one enterprise with 0
 * plan permissions and one with \>0
 */
export const fakeSubEnterprises: IOrganization[] = times(randomIn({
  min: 2,
  max: 20
}), () => ({
  ...generateFakeEnterprise(),
  accounts: times(randomIn({
    min: 0,
    max: 10
  }), () => ({
    id: faker.datatype.uuid()
  })),
  managers: times(randomIn({
    min: 0,
    max: 3
  }), () => ({
    id: faker.datatype.uuid(),
    email: fakeCommutifiEmail(),
    name: fakeFullName()
  })),
  worksites: times(randomIn({
    min: 1,
    max: 10
  }), () => fakeWorksite())
}));
export const fakeAnalyticsBuildings: BuildingFilter[] = [{
  buildingId: '6475a0c2-34e0-46f0-833a-111a03f0d90c',
  worksiteId: null,
  buildingOwnerEnterpriseId: '29be2907-6edf-4347-9c12-d83797829e11',
  buildingLocation: null,
  buildingName: null,
  buildingAddress: '1275 Av. des Canadiens-de-Montréal, Montréal, QC H3B 5E6, Canada',
  buildingStreetNumber: '1275',
  buildingStreet: 'Avenue des Canadiens-de-Montréal',
  buildingCity: 'Montréal',
  buildingState: 'Québec',
  buildingCountry: 'CA',
  buildingPostalCode: 'H3B5E6',
  buildingLatitude: 45.4964397,
  buildingLongitude: -73.5706624,
  buildingCreatedAt: null,
  buildingUpdatedAt: null,
  worksiteName: null,
  worksiteRegionDescription: null,
  worksiteDefaultTimezone: null,
  worksiteEnterpriseId: null,
  buildingOwnerEnterpriseName: null,
  buildingOwnerOrganizationId: null,
  buildingOwnerOrganizationName: null
}, {
  buildingId: '10222800-3057-41ac-bdf5-7c254dc5ca8c',
  worksiteId: null,
  buildingOwnerEnterpriseId: '29be2907-6edf-4347-9c12-d83797829e11',
  buildingLocation: null,
  buildingName: null,
  buildingAddress: '1600 Pennsylvania Avenue NW, Washington, DC 20500, USA',
  buildingStreetNumber: '1600',
  buildingStreet: 'Pennsylvania Avenue Northwest',
  buildingCity: 'Washington',
  buildingState: 'District of Columbia',
  buildingCountry: 'US',
  buildingPostalCode: '20500',
  buildingLatitude: 38.8976633,
  buildingLongitude: -77.0365739,
  buildingCreatedAt: null,
  buildingUpdatedAt: null,
  worksiteName: null,
  worksiteRegionDescription: null,
  worksiteDefaultTimezone: null,
  worksiteEnterpriseId: null,
  buildingOwnerEnterpriseName: null,
  buildingOwnerOrganizationId: null,
  buildingOwnerOrganizationName: null
}, {
  buildingId: 'ae8e8ae6-0965-4bee-8e59-ea782542ccf5',
  worksiteId: null,
  buildingOwnerEnterpriseId: '29be2907-6edf-4347-9c12-d83797829e11',
  buildingLocation: null,
  buildingName: null,
  buildingAddress: '1701 Rhode Island Ave NW, Washington, DC 20036, USA',
  buildingStreetNumber: '1701',
  buildingStreet: 'Rhode Island Avenue Northwest',
  buildingCity: 'Washington',
  buildingState: 'District of Columbia',
  buildingCountry: 'US',
  buildingPostalCode: '20036',
  buildingLatitude: 38.9067117,
  buildingLongitude: -77.0387573,
  buildingCreatedAt: null,
  buildingUpdatedAt: null,
  worksiteName: null,
  worksiteRegionDescription: null,
  worksiteDefaultTimezone: null,
  worksiteEnterpriseId: null,
  buildingOwnerEnterpriseName: null,
  buildingOwnerOrganizationId: null,
  buildingOwnerOrganizationName: null
}, {
  buildingId: '1071d62e-739d-445d-b138-f76a23bb83da',
  worksiteId: null,
  buildingOwnerEnterpriseId: '29be2907-6edf-4347-9c12-d83797829e11',
  buildingLocation: null,
  buildingName: null,
  buildingAddress: '400 Concar Dr, San Mateo, CA 94402, USA',
  buildingStreetNumber: '400',
  buildingStreet: 'Concar Drive',
  buildingCity: 'San Mateo',
  buildingState: 'California',
  buildingCountry: 'US',
  buildingPostalCode: '94402',
  buildingLatitude: 37.5532213,
  buildingLongitude: -122.3074963,
  buildingCreatedAt: null,
  buildingUpdatedAt: null,
  worksiteName: null,
  worksiteRegionDescription: null,
  worksiteDefaultTimezone: null,
  worksiteEnterpriseId: null,
  buildingOwnerEnterpriseName: null,
  buildingOwnerOrganizationId: null,
  buildingOwnerOrganizationName: null
}];
export const fakeEnterpriseCities = ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix'];
export const fakeEnterpriseAttributes = times<AttributeProps>(randomIn({
  min: 1,
  max: 2
}), () => fakeAttribute());
export const fakeEnterpriseManagers = times(50, i => ({
  id: faker.datatype.uuid(),
  name: `Manager ${i + 1}`,
  email: `manager${i + 1}@commutifi.com`
}));
export const fakeInvitedEnterpriseManagers = times(2, i => ({
  id: faker.datatype.uuid(),
  name: `Invited Manager ${i + 1}`,
  email: `invited.manager${i + 1}@commutifi.com`
}));
export const fakedShippingRates = fakeShippingRates();
export const fakedFundingInstructions = fakeFundingInstructions();
export const fakedEnterpriseBalances = fakeBalances();
export const fakedInventoryFilters = fakeInventoryFilters({}, 3);